import { Box, Chip, Collapse, Stack, Tooltip, Typography } from "@mui/material";
import {
  GlossaryEntry,
  grainLabel,
  isHidden,
  isMetric,
} from "../../../models/Glossary";

const MONO = "ui-monospace, SFMono-Regular, Menlo, monospace";

/**
 * Names sit in a fixed column so they line up vertically down the page. With the
 * name inline ahead of the description, every row started at a different offset
 * and the list could only be read one row at a time.
 */
const NAME_COL = "230px";
const META_COL = "116px";

/** Only non-active states get a chip — a status chip has to read as an exception. */
function StatusChip({ entry }: { entry: GlossaryEntry }) {
  if (entry.status === "active") return null;
  const rare = entry.status === "rare";
  const label = rare ? "rarely used" : entry.status;
  return (
    <Tooltip title={entry.statusReason ?? ""} arrow placement="top">
      <Chip
        label={label}
        size="small"
        sx={{
          height: 16,
          borderRadius: "2px",
          fontSize: "0.6rem",
          textTransform: "uppercase",
          fontWeight: 700,
          letterSpacing: "0.3px",
          flexShrink: 0,
          "& .MuiChip-label": { px: 0.6 },
          bgcolor: rare ? "#eeeeee" : "#f3e2db",
          color: rare ? "rgba(0,0,0,0.55)" : "#8c3a1c",
        }}
      />
    </Tooltip>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <Box sx={{ mb: 1.25 }}>
      <Typography
        sx={{
          fontSize: "0.65rem",
          textTransform: "uppercase",
          fontWeight: 700,
          color: "rgba(0,0,0,0.45)",
          letterSpacing: "0.5px",
          mb: 0.25,
        }}
      >
        {label}
      </Typography>
      {children}
    </Box>
  );
}

function Sql({ children }: { children: string }) {
  return (
    <Box
      component="pre"
      sx={{
        m: 0,
        p: 0.75,
        bgcolor: "#fbfbfb",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: "3px",
        fontFamily: MONO,
        fontSize: "0.72rem",
        lineHeight: 1.55,
        // A CASE expression runs long; it scrolls inside its own box rather than
        // forcing the page to scroll sideways.
        overflowX: "auto",
        whiteSpace: "pre-wrap",
        wordBreak: "break-word",
      }}
    >
      {children}
    </Box>
  );
}

function ValueChips({ values, truncated }: { values: string[]; truncated?: number }) {
  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
      {values.map((v) => (
        <Chip
          key={v}
          label={v}
          size="small"
          sx={{
            height: 20,
            borderRadius: "3px",
            fontSize: "0.7rem",
            fontFamily: MONO,
            bgcolor: "#eceff1",
            "& .MuiChip-label": { px: 0.75 },
          }}
        />
      ))}
      {!!truncated && (
        <Typography sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.5)", alignSelf: "center" }}>
          +{truncated} more
        </Typography>
      )}
    </Stack>
  );
}

function Stats({ entry }: { entry: GlossaryEntry }) {
  const s = entry.stats;
  if (!s) return null;
  const parts: string[] = [];
  if (s.queries) parts.push(`${s.queries.toLocaleString()} queries`);
  if (s.savedQueries) parts.push(`${s.savedQueries} saved`);
  if (s.users) parts.push(`${s.users} users`);
  if (s.lastUsed) parts.push(`last used ${s.lastUsed}`);
  if (typeof s.populatedPct === "number") {
    parts.push(`${Math.round(s.populatedPct * 100)}% populated`);
  }
  if (!parts.length) return null;
  return (
    <Typography sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.5)" }}>
      {parts.join(" · ")}
    </Typography>
  );
}

interface Props {
  entry: GlossaryEntry;
  expanded: boolean;
  onToggle: () => void;
  /** Title of the guide that covers this entry, when there is one. */
  guideTitle?: string;
  onOpenGuide?: (guideId: string) => void;
}

export default function GlossaryEntryRow({
  entry,
  expanded,
  onToggle,
  guideTitle,
  onOpenGuide,
}: Props) {
  const metric = isMetric(entry);
  // A metric's formula is its summary line: more precise than a sentence, and
  // unlike a description every metric has one.
  const summary = metric ? entry.formula : entry.description;
  const meta = metric ? entry.format || "count" : grainLabel(entry.grain);

  return (
    <Box
      sx={{
        borderLeft: "3px solid",
        borderLeftColor: expanded ? "#4c4c4c" : "transparent",
        borderBottom: "1px solid",
        borderBottomColor: expanded ? "#d0d0d0" : "#ededed",
        borderTop: expanded ? "1px solid #d0d0d0" : "none",
        bgcolor: expanded ? "#fff" : "transparent",
        boxShadow: expanded ? "0 1px 3px rgba(0,0,0,0.10)" : "none",
        position: expanded ? "relative" : "static",
        zIndex: expanded ? 1 : "auto",
        "&:hover": { bgcolor: expanded ? "#fff" : "#f7f7f7" },
      }}
    >
      <Box
        onClick={onToggle}
        sx={{
          px: 1.25,
          py: 0.6,
          cursor: "pointer",
          display: "grid",
          gridTemplateColumns: {
            xs: "1fr",
            md: `${NAME_COL} ${META_COL} minmax(0, 1fr)`,
          },
          alignItems: "baseline",
          columnGap: 1.5,
          rowGap: 0.25,
        }}
      >
        <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
          <Typography
            sx={{
              fontFamily: MONO,
              fontSize: "0.78rem",
              fontWeight: 600,
              color: expanded ? "#1a1a1a" : "inherit",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {entry.name}
          </Typography>
          <StatusChip entry={entry} />
        </Stack>

        <Typography
          sx={{
            fontSize: "0.68rem",
            color: "rgba(0,0,0,0.45)",
            whiteSpace: "nowrap",
            textTransform: "lowercase",
          }}
        >
          {meta}
        </Typography>

        <Typography
          sx={{
            fontSize: "0.75rem",
            color: "rgba(0,0,0,0.65)",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontFamily: metric ? MONO : undefined,
            minWidth: 0,
          }}
        >
          {summary}
        </Typography>
      </Box>

      <Collapse in={expanded} unmountOnExit>
        <Box
          sx={{
            px: 1.5,
            pb: 1.5,
            pt: 1,
            ml: { xs: 0, md: `calc(${NAME_COL} + 12px)` },
            borderTop: "1px dashed",
            borderTopColor: "#e4e4e4",
          }}
        >
          {entry.description ? (
            <Field
              label={`Definition${
                entry.descriptionSource ? ` — ${entry.descriptionSource}` : ""
              }`}
            >
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "pre-line" }}>
                {entry.description}
              </Typography>
            </Field>
          ) : (
            !metric && (
              <Field label="Definition">
                <Typography sx={{ fontSize: "0.8rem", color: "rgba(0,0,0,0.45)" }}>
                  Not yet documented — no tracking provider describes this column,
                  it is one of ours.
                </Typography>
              </Field>
            )
          )}

          {entry.note && (
            <Field label="Note">
              <Typography sx={{ fontSize: "0.8rem", whiteSpace: "pre-line" }}>
                {entry.note}
              </Typography>
            </Field>
          )}

          {entry.guide && guideTitle && onOpenGuide && (
            <Field label="Guide">
              <Typography
                component="button"
                type="button"
                onClick={() => onOpenGuide(entry.guide as string)}
                sx={{
                  p: 0,
                  border: 0,
                  bgcolor: "transparent",
                  cursor: "pointer",
                  font: "inherit",
                  fontSize: "0.8rem",
                  color: "#CF0A2C",
                  textAlign: "left",
                  "&:hover, &:focus-visible": { textDecoration: "underline" },
                }}
              >
                {guideTitle}
              </Typography>
            </Field>
          )}

          {metric ? (
            <>
              <Field label="Formula">
                <Sql>{entry.formula}</Sql>
              </Field>
              {entry.components.length > 0 && (
                <Field label="Built from">
                  <Stack spacing={0.75}>
                    {entry.components.map((c) => (
                      <Box key={c.name}>
                        <Typography sx={{ fontFamily: MONO, fontSize: "0.72rem", mb: 0.25 }}>
                          {c.name}
                          <Box component="span" sx={{ color: "rgba(0,0,0,0.45)" }}>
                            {" · "}
                            {c.category}
                          </Box>
                        </Typography>
                        <Sql>{c.condition}</Sql>
                      </Box>
                    ))}
                  </Stack>
                </Field>
              )}
            </>
          ) : (
            <>
              {entry.values && entry.values.length > 0 && (
                <Field label="Values">
                  <ValueChips values={entry.values} truncated={entry.valuesTruncated} />
                </Field>
              )}
              <Field label={entry.computed ? "Expression (computed)" : "Column"}>
                <Sql>{entry.expression}</Sql>
              </Field>
              <Stack direction="row" spacing={2.5} flexWrap="wrap" sx={{ mb: 1.25 }}>
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.55)" }}>
                  <b>Type:</b> {entry.type}
                  {entry.uuidType ? ` (${entry.uuidType})` : ""}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.55)" }}>
                  <b>Grain:</b> {grainLabel(entry.grain)}
                </Typography>
                <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.55)" }}>
                  <b>Operators:</b> {entry.ops.join(", ")}
                </Typography>
                {entry.joins.length > 0 && (
                  <Typography sx={{ fontSize: "0.72rem", color: "rgba(0,0,0,0.55)" }}>
                    <b>Joins:</b> {entry.joins.join(" → ")}
                  </Typography>
                )}
              </Stack>
            </>
          )}

          {entry.seasonCoverage && (
            <Typography sx={{ fontSize: "0.75rem", color: "warning.dark", mb: 1 }}>
              {entry.seasonCoverage}
            </Typography>
          )}

          {isHidden(entry) && entry.statusReason && (
            <Typography sx={{ fontSize: "0.75rem", color: "#8c3a1c", mb: 1 }}>
              Not offered in the query builder — {entry.statusReason}. Saved
              queries that already use it still run.
            </Typography>
          )}

          <Stack direction="row" spacing={2} flexWrap="wrap" alignItems="center">
            <Typography sx={{ fontSize: "0.7rem", color: "rgba(0,0,0,0.5)" }}>
              Available in: {entry.registeredIn.join(", ") || "—"}
            </Typography>
            <Stats entry={entry} />
          </Stack>
        </Box>
      </Collapse>
    </Box>
  );
}
